import React, { useEffect, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import './css/EmailSettingsPage.css';

const defaultBodyTemplate = [
  'Hello,',
  '',
  'Please see attached {document}{documentNumber}.',
  '',
  'Thank You',
  '',
  '{businessName}',
  '{businessAddress1}',
  '{businessCityLine}',
  '{businessPhone}',
  '{businessWebsite}',
  '{businessEmail}',
].join('\n');

const templateTabs = [
  { key: 'invoiceBodyTemplate', label: 'Invoice' },
  { key: 'transferBodyTemplate', label: 'Transfer' },
  { key: 'transferReturnBodyTemplate', label: 'Transfer Return' },
  { key: 'collectionReportBodyTemplate', label: 'Collection Report' },
  { key: 'paymentBodyTemplate', label: 'Payment' },
  { key: 'repairBodyTemplate', label: 'Repair' },
  { key: 'returnBodyTemplate', label: 'Return' },
  { key: 'stockCheckBodyTemplate', label: 'Stock Check' },
  { key: 'locationStockBodyTemplate', label: 'Location Stock' },
];

const emptySettings = {
  server: '',
  username: '',
  password: '',
  port: 587,
  useSsl: true,
  sendHtml: true,
  fromName: "Irene's Wigs",
  fromEmail: '',
  bodyTemplate: defaultBodyTemplate,
  templateOptions: {},
  ...Object.fromEntries(templateTabs.map(({ key }) => [key, defaultBodyTemplate])),
  hasPassword: false,
};

export function EmailSettingsPanel({ updateTitle = true }) {
  const [settings, setSettings] = useState(emptySettings);
  const [testTo, setTestTo] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTemplateKey, setActiveTemplateKey] = useState(templateTabs[0].key);
  const metadataKey = activeTemplateKey.replace(/BodyTemplate$/, '');
  const metadata = settings.templateOptions?.[metadataKey] || { subject: '', attachmentName: '' };
  const updateMetadata = (field, value) => setSettings(current => ({
    ...current,
    templateOptions: {
      ...current.templateOptions,
      [metadataKey]: { subject: '', attachmentName: '', ...current.templateOptions?.[metadataKey], [field]: value },
    },
  }));

  const updateField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadSettings = async () => {
    setError('');
    try {
      const res = await api.get('/email/settings');
      setSettings({
        ...emptySettings,
        ...res.data,
        password: '',
      });
      setTestTo(res.data?.fromEmail || res.data?.username || '');
    } catch (err) {
      console.error('Failed to load email settings:', err);
      setError(err.response?.data?.error || 'Failed to load email settings.');
    }
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus('');
    setError('');
    try {
      const payload = {
        ...settings,
        port: Number(settings.port || 587),
      };
      const res = await api.put('/email/settings', payload);
      setSettings({
        ...emptySettings,
        ...res.data,
        password: '',
      });
      setStatus('Email settings saved.');
    } catch (err) {
      console.error('Failed to save email settings:', err);
      setError(err.response?.data?.error || 'Failed to save email settings.');
    } finally {
      setSaving(false);
    }
  };

  const testSettings = async () => {
    setTesting(true);
    setStatus('');
    setError('');
    try {
      const res = await api.post('/email/test', { to: testTo });
      const recipients = (res.data?.recipients || []).join(', ');
      setStatus(recipients ? `Test email sent to ${recipients}.` : 'Test email sent.');
    } catch (err) {
      console.error('Failed to send test email:', err);
      setError(err.response?.data?.error || 'Failed to send test email.');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    if (updateTitle) {
      document.title = "Email Settings - Services Irene's Wigs.com";
    }
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTitle]);

  return (
    <form className="email-settings-panel" onSubmit={saveSettings}>
      <header className="email-settings-header">
        <div>
          <h2>Email Settings</h2>
          <p>SMTP settings and templates used when sending emails.</p>
        </div>
        <button type="submit" className="email-primary-button" disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </header>

      {status && <div className="email-status success">{status}</div>}
      {error && <div className="email-status error">{error}</div>}

      <section className="email-settings-section">
        <h3>SMTP Email Settings</h3>
        <div className="email-settings-grid">
          <label>
            Server
            <input
              type="text"
              value={settings.server}
              onChange={(e) => updateField('server', e.target.value)}
              placeholder="smtp.office365.com"
              required
            />
          </label>
          <label>
            Port
            <input
              type="number"
              min="1"
              max="65535"
              value={settings.port}
              onChange={(e) => updateField('port', e.target.value)}
              required
            />
          </label>
          <label>
            Username
            <input
              type="email"
              value={settings.username}
              onChange={(e) => updateField('username', e.target.value)}
              placeholder="info@ireneswigs.com"
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={settings.password}
              onChange={(e) => updateField('password', e.target.value)}
              placeholder={settings.hasPassword ? 'Leave blank to keep current password' : ''}
              autoComplete="new-password"
            />
          </label>
          <label>
            From Name
            <input
              type="text"
              value={settings.fromName}
              onChange={(e) => updateField('fromName', e.target.value)}
              placeholder="Irene's Wigs"
            />
          </label>
          <label>
            From Email
            <input
              type="email"
              value={settings.fromEmail}
              onChange={(e) => updateField('fromEmail', e.target.value)}
              placeholder="info@ireneswigs.com"
            />
          </label>
        </div>

        <div className="email-checkbox-row">
          <label>
            <input
              type="checkbox"
              checked={settings.useSsl}
              onChange={(e) => updateField('useSsl', e.target.checked)}
            />
            Use SSL / STARTTLS
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.sendHtml}
              onChange={(e) => updateField('sendHtml', e.target.checked)}
            />
            Send in HTML format
          </label>
        </div>
      </section>

      <section className="email-settings-section">
        <h3>Email Templates</h3>
        <div className="email-template-tabs" role="tablist" aria-label="Email templates">
          {templateTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTemplateKey === tab.key}
              className={activeTemplateKey === tab.key ? 'active' : ''}
              onClick={() => setActiveTemplateKey(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="email-settings-grid email-template-metadata">
          <label>
            Subject
            <input value={metadata.subject || ''} maxLength={300}
              onChange={event => updateMetadata('subject', event.target.value)}
              placeholder="Leave blank to use the current default" />
          </label>
          <label>
            Attachment filename
            <input value={metadata.attachmentName || ''} maxLength={200}
              onChange={event => updateMetadata('attachmentName', event.target.value)}
              placeholder="e.g. {document}{documentNumber}" />
          </label>
        </div>
        <p className="email-template-help email-template-metadata">
          Leave either field blank to keep its current default. The attachment extension is added automatically for PDF or Excel.
        </p>
        <label className="email-template-field">
          Body
          <textarea
            value={settings[activeTemplateKey] || ''}
            onChange={(e) => updateField(activeTemplateKey, e.target.value)}
          />
        </label>
        <p className="email-template-help">
          Available placeholders: {'{document}'}, {'{documentNumber}'}, {'{recipientName}'}, {'{customerName}'}, {'{locationName}'}, {'{itemName}'}, {'{businessName}'}, {'{businessAddress1}'}, {'{businessCityLine}'}, {'{businessPhone}'}, {'{businessWebsite}'}, {'{businessEmail}'}.
        </p>
      </section>

      <section className="email-settings-section">
        <h3>Test Email Settings</h3>
        <div className="email-test-row">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Send test email to"
          />
          <button
            type="button"
            className="email-secondary-button"
            onClick={testSettings}
            disabled={testing}
          >
            {testing ? 'Testing...' : 'Test Email Settings'}
          </button>
        </div>
      </section>
    </form>
  );
}

function EmailSettingsPage() {
  return (
    <>
      <TopNav />
      <main className="email-settings-page">
        <EmailSettingsPanel />
      </main>
    </>
  );
}

export default EmailSettingsPage;
